import { AllReportYearsBase } from 'npo/pages/ReportsBeta/api';
import { getReportName } from 'npo/pages/ReportsBeta/ReportGroup';
import React, { useCallback, useEffect, useState } from 'react';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import styled from 'styled-components';
import ReportSharingToggle from './ReportSharingToggle';
import { ReportVisibility } from 'shared/auth/hooks/useUserActions';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { useClient } from 'shared/apollo';
import { updateReportVisibility } from './api';
import TrueImpactFloatLoading from 'shared/design-system/components/TrueImpactFloatLoading';
import { donorNamePublicDisabledReason } from 'shared/components/WelcomeBack/ReportVisibilityModal';

const tooltip = 'You can choose visibility for this report.';

interface IndividualReportSharingProps {
  index: number;
  isLastIndex: boolean;
  report: AllReportYearsBase;
  program: {
    Id: number;
    name: string;
    managerId: number;
  };
}

const StyledDiv = styled.div<{ isLastIndex: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACERS.small};
  padding: ${SPACERS.tiny} ${SPACERS.medium} ${SPACERS.tiny}
    ${SPACERS.xExtraLarge};
  transition: background-color 0.2s ease-in-out;
  ${({ isLastIndex }) =>
    isLastIndex ? '' : `border-bottom: 1px solid ${COLORS.grayLight};`}
  &:hover {
    background-color: ${COLORS.ctaPrimaryBackgroundLighter};
  }
  .report-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${COLORS.grayDark};
  }
`;

const IndividualReportSharing: React.FC<IndividualReportSharingProps> = ({
  report,
  isLastIndex,
}) => {
  const client = useClient();
  const [requestState, makeRequest] = useGraphQLRequest();
  const [visibility, setVisibility] = useState<ReportVisibility>(
    report.visibility,
  );

  useEffect(() => {
    setVisibility(report.visibility);
  }, [report.visibility]);

  const handleToggle = useCallback(
    (value: ReportVisibility) => {
      if (!client) return;
      if (value === 'PUBLIC' && report.level1DonorName) return;
      setVisibility(value);
      makeRequest(
        updateReportVisibility(client, {
          reportId: report.dbId,
          visibility: value,
        }),
      );
    },
    [visibility, report.level1DonorName],
  );

  return (
    <StyledDiv isLastIndex={isLastIndex}>
      <TrueImpactFloatLoading
        requestState={requestState}
        loadingMessage="Saving report's visibility"
        successMessage="Report's visibility saved successfully"
        errorMessage="Failed to save report's visibility"
      />
      <TrueImpactText className="report-name">
        <span title={getReportName(report)}>{getReportName(report)}</span>
      </TrueImpactText>
      <ReportSharingToggle
        tooltip={tooltip}
        visibility={visibility}
        publicDisabledReason={
          report.level1DonorName ? donorNamePublicDisabledReason : undefined
        }
        handleToggle={handleToggle}
      />
    </StyledDiv>
  );
};

export default IndividualReportSharing;
